/**
 * main-collection.js
 *
 * Handles:
 *   - Mobile filter drawer toggle (open/close, escape key, body scroll lock)
 *   - Sort dropdown auto-submit on change
 *   - Active filter count badge update
 *   - Auto-submit filters on checkbox change (desktop progressive enhancement)
 */

(function () {
  'use strict';

  const sections = document.querySelectorAll('[data-section-type="main-collection"]');

  sections.forEach(initSection);

  function initSection(section) {
    const filterToggle = section.querySelector('[data-filter-toggle]');
    const filtersPanel = section.querySelector('[data-filters-panel]');
    const filterClose = section.querySelector('[data-filter-close]');
    const sortSelect = section.querySelector('[data-sort-select]');
    const filterForm = section.querySelector('[data-filter-form]');
    const activeFilterCountEl = section.querySelector('[data-active-filter-count]');

    /* ----- Mobile filter drawer ----- */
    if (filterToggle && filtersPanel) {
      filterToggle.addEventListener('click', () => openDrawer(filtersPanel, filterToggle));
    }

    if (filterClose && filtersPanel) {
      filterClose.addEventListener('click', () => closeDrawer(filtersPanel, filterToggle));
    }

    if (filtersPanel) {
      // Click backdrop to close
      filtersPanel.addEventListener('click', (e) => {
        if (e.target === filtersPanel) {
          closeDrawer(filtersPanel, filterToggle);
        }
      });
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && filtersPanel && filtersPanel.dataset.open === 'true') {
        closeDrawer(filtersPanel, filterToggle);
      }
    });

    /* ----- Sort dropdown ----- */
    if (sortSelect) {
      sortSelect.addEventListener('change', () => {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', sortSelect.value);
        url.searchParams.delete('page'); // reset to page 1 on sort change
        window.location.href = url.toString();
      });
    }

    /* ----- Active filter count badge ----- */
    if (activeFilterCountEl && filterForm) {
      updateActiveFilterCount(filterForm, activeFilterCountEl);
      filterForm.addEventListener('change', () => updateActiveFilterCount(filterForm, activeFilterCountEl));
    }

    /* ----- Optional: auto-submit on desktop ----- */
    // Uncomment to make checkbox changes auto-submit the form on desktop only.
    // if (filterForm && window.matchMedia('(min-width: 990px)').matches) {
    //   filterForm.addEventListener('change', (e) => {
    //     if (e.target.type === 'checkbox') {
    //       filterForm.submit();
    //     }
    //   });
    // }
  }

  function openDrawer(panel, toggle) {
    panel.dataset.open = 'true';
    document.body.style.overflow = 'hidden';
    if (toggle) toggle.setAttribute('aria-expanded', 'true');
    // Focus the close button for keyboard users
    const closeBtn = panel.querySelector('[data-filter-close]');
    if (closeBtn) closeBtn.focus();
  }

  function closeDrawer(panel, toggle) {
    panel.dataset.open = 'false';
    document.body.style.overflow = '';
    if (toggle) {
      toggle.setAttribute('aria-expanded', 'false');
      toggle.focus();
    }
  }

  function updateActiveFilterCount(form, badge) {
    const checked = form.querySelectorAll('input[type="checkbox"]:checked').length;
    const priceInputs = form.querySelectorAll('[data-price-range] input[type="number"]');
    let priceActive = 0;
    priceInputs.forEach((input) => {
      if (input.value && input.value !== input.placeholder) priceActive = 1;
    });
    const total = checked + priceActive;
    badge.textContent = total;
    badge.dataset.activeFilterCount = total;
  }
})();

/**
 * Wishlist (product card heart icon)
 *
 * Local-only persistence via localStorage. Designed as a starting point —
 * swap the read/write functions below for your real wishlist API
 * (Shopify customer metafield, third-party app, etc.) without changing markup.
 *
 * Uses event delegation on document so it works for cards rendered after page load
 * (e.g. predictive search, infinite scroll if you later add it).
 */
(function () {
  'use strict';

  const STORAGE_KEY = 'sutracraft:wishlist';

  function readWishlist() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    } catch {
      return [];
    }
  }

  function writeWishlist(handles) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
    } catch {
      /* storage full or unavailable — fail quietly */
    }
  }

  function isInWishlist(handle) {
    return readWishlist().includes(handle);
  }

  function toggleInWishlist(handle) {
    const list = readWishlist();
    const index = list.indexOf(handle);
    if (index === -1) {
      list.push(handle);
    } else {
      list.splice(index, 1);
    }
    writeWishlist(list);
    return index === -1; // returns new pressed state
  }

  /* Sync state on load — mark already-saved items as pressed */
  function hydrateButtons() {
    const buttons = document.querySelectorAll('[data-wishlist-toggle]');
    const saved = readWishlist();
    buttons.forEach((btn) => {
      const handle = btn.dataset.productHandle;
      if (handle && saved.includes(handle)) {
        btn.setAttribute('aria-pressed', 'true');
        const title = btn.getAttribute('aria-label') || '';
        btn.setAttribute('aria-label', title.replace('Add ', 'Remove ').replace(' to wishlist', ' from wishlist'));
      }
    });
  }

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();

    const handle = btn.dataset.productHandle;
    if (!handle) return;

    const nowPressed = toggleInWishlist(handle);
    btn.setAttribute('aria-pressed', String(nowPressed));

    /* Update aria-label so screen readers announce the new action */
    const title = btn.getAttribute('aria-label') || '';
    if (nowPressed) {
      btn.setAttribute('aria-label', title.replace('Add ', 'Remove ').replace(' to wishlist', ' from wishlist'));
    } else {
      btn.setAttribute('aria-label', title.replace('Remove ', 'Add ').replace(' from wishlist', ' to wishlist'));
    }

    /* Custom event hook — listen for this if integrating a third-party wishlist */
    document.dispatchEvent(
      new CustomEvent('wishlist:toggle', {
        detail: { handle, productId: btn.dataset.productId, pressed: nowPressed }
      })
    );
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', hydrateButtons);
  } else {
    hydrateButtons();
  }
})();
